/**
 * i18n context types and interfaces
 *
 * Defines the types used for internationalization (i18n) throughout the application,
 * including language codes, message structures, and the i18n context interface.
 */

import { ClientType } from '../client-type.type';
import type { LocationCountryCode } from 'src/api/generated';
import type { TranslationKey } from './translation-keys.type';

/** Supported language codes in the application */
export type LanguageCode = 'es-CL' | 'en-US';

/** Array of all supported language codes */
export const SUPPORTED_LANGUAGES: LanguageCode[] = ['es-CL', 'en-US'];

/** Translation messages organized by language code */
export type MessagesByLanguage = Record<LanguageCode, Record<string, string>>;

/** Client-type specific translation overrides organized by language and client type */
export type ClientTypeOverrides = Record<
  LanguageCode,
  Record<ClientType, Partial<Record<string, string>>>
>;

/** Country-specific client-type overrides organized by language, client type and tenant country */
export type CountryClientTypeOverrides = Record<
  LanguageCode,
  Partial<
    Record<
      ClientType,
      Partial<Record<LocationCountryCode, Partial<Record<string, string>>>>
    >
  >
>;

/** Parameters for dynamic translation string interpolation */
export type TranslateParams = Record<string, string | number | undefined>;

/** Options for resolving translations */
export interface ResolveTranslationOptions {
  /** Client type to use for resolving client-specific overrides */
  clientType?: ClientType;
  /** Tenant country code used to resolve country-specific terminology overrides */
  tenantCountryCode?: LocationCountryCode | null;
  /** Whether to apply client type overrides when resolving translations */
  allowClientOverrides?: boolean;
}

/** Context value provided by the I18nProvider */
export interface I18nContextValue {
  /** Current language code */
  language: LanguageCode;
  /** Function to change the current language */
  setLanguage: (language: LanguageCode) => void;
  /** Current client type */
  clientType: ClientType;
  /** Function to translate a message key with optional parameters - accepts only valid translation keys */
  t: (key: TranslationKey, params?: TranslateParams) => string;
}
